import { useEffect, useState, useMemo } from 'react';

import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { IUserQuizzes } from '@quizzach/interfaces';
import { useAuth } from '@quizzach/contexts';

export const useUserQuizzes = () => {
	const [rawQuizzes, setRawQuizzes] = useState<IUserQuizzes[]>();
	const [loading, setLoading] = useState(false);
	const { profileInfo } = useAuth();

	const getUserQuizzes = async () => {
		setLoading(true);
		try {
			const response = await fetchData<Partial<IUserQuizzes[]>>(
				`/api/quizzes`,
				{ method: 'GET' },
				{
					authRoute: true,
				}
			);
			if (response.status >= 300) {
				consoleDev('getUserQuizzes', ConsoleStatus.NET, response.status);
			} else {
				consoleDev('getUserQuizzes', ConsoleStatus.SUCC);

				if (response.data) {
					// Store raw data and defer processing to useMemo
					setRawQuizzes(response.data as IUserQuizzes[]);
				}
			}
		} catch (err) {
			consoleDev('getUserQuizzes', ConsoleStatus.HTTP, err);
		} finally {
			setLoading(false);
		}
	};

	// Memoize expensive data transformation
	const { quizzesToList, authoredQuizzesToList } = useMemo(() => {
		if (!rawQuizzes) {
			return { quizzesToList: undefined, authoredQuizzesToList: undefined };
		}

		const authored: IUserQuizzes[] = [];
		const others: IUserQuizzes[] = [];

		// Process and sort data; fresh = never opened (backend flips it on
		// first quiz view), so unplayed quizzes surface first, newest within
		rawQuizzes
			.map((item) => new IUserQuizzes(item))
			.sort(
				(a, b) =>
					Number(b.fresh) - Number(a.fresh) || b.created_at - a.created_at
			)
			.forEach((quiz) => {
				if (
					quiz.quiz_type === 'premade' &&
					quiz.premade_quiz?.author?.display_name_slug ===
						profileInfo?.display_name_slug
				) {
					authored.push(quiz);
				} else {
					others.push(quiz);
				}
			});

		return { quizzesToList: others, authoredQuizzesToList: authored };
	}, [rawQuizzes, profileInfo?.display_name_slug]);

	useEffect(() => {
		if (profileInfo) {
			getUserQuizzes();
		}
	}, [profileInfo]);

	return {
		quizzesToList,
		authoredQuizzesToList,
		loading,
		setLoading,
	};
};
